import _ from "lodash";
import { generateRandomId } from "Utils";
import { useRef, useCallback, useEffect } from "react";
import { getLastQuestionIdFromQuestions } from "Worksheet/modules/WorksheetUtils";
import { isWorksheetQueriesV2Enabled } from "Worksheet/modules/WorksheetFeatureFlags";
import {
  getQuestionTemplate,
  getQuestionTemplateV2,
} from "../modules/AiQuestionTemplates";
import {
  processAndCreateFields,
  getStreamedQuestionChunks,
  getAllFieldsFromParsedFields,
  parseStreamedXmlToQuestionFields,
} from "../modules/AiQuestionLoaderUtils";
import {
  GENERATE_USING_AI,
  AI_GEN_IDENTIFIER,
  QUESTION_START_TAG,
  FIELD_SET_NAMES_V1,
  FIELD_SET_NAMES_V2,
  TYPE_OF_QUESTION_TAG,
  AI_SUPPORTED_QUESTIONS,
  QUESTION_TYPE_TEXT_RESPONSE,
} from "../modules/AiQuestionLoaderConstant";

// * Checks whether the given ID corresponds to an AI-generated question.
const isAiQuestion = id => {
  return _.isEqual(id, GENERATE_USING_AI) || _.includes(id, AI_GEN_IDENTIFIER);
};

// * Extracts the question type from the parsed fields of a streamed question chunk.
const getQuestionTypeFromParsedFields = parsedFields => {
  const toqTag = _.get(parsedFields, ["0", TYPE_OF_QUESTION_TAG], "``");
  return _.capitalize(toqTag?.replace(/`/g, ""));
};

// * Generates a unique set of field IDs for a new AI-generated question based on the active query version.
const getFieldSet = () => {
  const isQueriesV2Enabled = isWorksheetQueriesV2Enabled();
  const FIELD_SET = isQueriesV2Enabled
    ? FIELD_SET_NAMES_V2
    : FIELD_SET_NAMES_V1;
  const setAiID = () => `${AI_GEN_IDENTIFIER}${generateRandomId()}`;
  return _.mapValues(_.keyBy(FIELD_SET), setAiID);
};

/**
 * Hook that processes a streamed AI response and progressively builds
 * question objects in the local cache as each question chunk arrives.
 *
 * Responsibilities:
 *  1. Extracts individual question chunks from the streaming message.
 *  2. Parses each chunk into structured field data (title, choices, answer, etc.).
 *  3. Updates the corresponding cached question fields in real-time.
 *  4. Creates a new cached question template whenever a new chunk is detected.
 */
export const useAiQuestionStream = props => {
  const {
    messages,
    sections,
    questionId,
    parentItemId,
    parentResourceId,
    questionResourceId,
    activeQuestionId,
    updateQuestionToCache,
    updateQuestionToCacheV2,
    isAddingQuestionsInBetween,
  } = props;

  const fieldIdsRef = useRef({});
  const fieldIdsMapRef = useRef({});
  const processedFieldsRef = useRef({});
  const generatedQuestionCacheCountRef = useRef(0);
  const isQueriesV2Enabled = isWorksheetQueriesV2Enabled();

  /**
   * @deprecated Handles question cache creation using the legacy (V1) data model.
   *
   * Generates a unique field set, builds a V1 question template, and
   * pushes it into the cache via `updateQuestionToCache`.
   *
   * @param {string} questionType - The type of question (e.g. "Multiple choice").
   * @returns {boolean} Whether the question was successfully created.
   */
  const handleUpdateV1 = useCallback(
    questionType => {
      const lastSectionId = _.get(_.last(sections), "id", null);
      if (!parentResourceId && !lastSectionId) return false;

      const fieldSet = getFieldSet();
      fieldIdsRef.current = fieldSet;

      const newQuestion = getQuestionTemplate(fieldSet, questionType);
      const switchId = isAiQuestion(activeQuestionId)
        ? fieldSet.questionId
        : null;

      const questionOffset = generatedQuestionCacheCountRef.current;
      const enableQuestionSwitch = questionOffset === 0;

      updateQuestionToCache({
        newQuestion,
        questionOffset,
        questionResourceId,
        enableQuestionSwitch,
        isAddingQuestionsInBetween,
        questionId: switchId,
        parentResourceId: parentResourceId || lastSectionId,
      });

      return true;
    },
    [
      sections,
      activeQuestionId,
      parentResourceId,
      questionResourceId,
      updateQuestionToCache,
      isAddingQuestionsInBetween,
    ]
  );

  /**
   * Handles question cache creation using the V2 data model.
   *
   * Generates a unique field set, builds a V2 question template, and
   * pushes it into the cache via `updateQuestionToCacheV2`.
   *
   * @param {string} questionType - The type of question (e.g. "Multiple choice").
   * @returns {boolean} Whether the question was successfully created.
   */
  const handleUpdateV2 = useCallback(
    questionType => {
      const lastSectionItemId = _.get(_.last(sections), "item.id", null);
      const targetParentItemId = parentItemId || lastSectionItemId;
      if (_.isEmpty(targetParentItemId)) return false;

      const fieldSet = getFieldSet();
      fieldIdsRef.current = fieldSet;

      const newQuestion = getQuestionTemplateV2(fieldSet, questionType);
      const switchId = isAiQuestion(activeQuestionId)
        ? fieldSet.questionId
        : null;

      const lastQuestionId = getLastQuestionIdFromQuestions(sections);
      const questionOffset = generatedQuestionCacheCountRef.current;
      const enableQuestionSwitch = questionOffset === 0;

      updateQuestionToCacheV2({
        newQuestion,
        questionOffset,
        enableQuestionSwitch,
        isAddingQuestionsInBetween,
        questionId: switchId,
        parentItemId: targetParentItemId,
        putAfterQuestionId: questionId || lastQuestionId,
      });

      return true;
    },
    [
      sections,
      questionId,
      parentItemId,
      activeQuestionId,
      updateQuestionToCacheV2,
      isAddingQuestionsInBetween,
    ]
  );

  // * Invoke the appropriate cache update handler based on the feature flag status
  // * Stores field IDs per question index and only increments counter on success
  const handleQuestionCreation = useCallback(
    (questionType, questionIndex) => {
      const success = isQueriesV2Enabled
        ? handleUpdateV2(questionType)
        : handleUpdateV1(questionType);

      if (success) {
        fieldIdsMapRef.current[questionIndex] = fieldIdsRef.current;
        generatedQuestionCacheCountRef.current += 1;
      }
    },
    [isQueriesV2Enabled, handleUpdateV1, handleUpdateV2]
  );

  /**
   * Effect: Runs on every new streamed message batch.
   *
   * Iterates ALL question chunks sequentially (not just the last one).
   * For each question, iterates ALL parsed fields (not just the last one).
   * This ensures no field data is lost when streaming is faster than React renders.
   *
   * - Creates a new cached question template when a new chunk is detected.
   * - Writes complete fields exactly once (tracked via processedFieldsRef).
   * - Writes in-progress (last) fields on every render with latest value.
   */
  useEffect(() => {
    const questions = getStreamedQuestionChunks(messages);
    if (_.isEmpty(questions)) return;

    _.forEach(questions, (question, questionIndex) => {
      const text = _.get(question, "text", "");
      if (!_.includes(text, QUESTION_START_TAG)) return;

      const parseFields = parseStreamedXmlToQuestionFields(text || "");
      const rawQuestionType = getQuestionTypeFromParsedFields(parseFields);
      const questionType = _.includes(AI_SUPPORTED_QUESTIONS, rawQuestionType)
        ? rawQuestionType
        : QUESTION_TYPE_TEXT_RESPONSE;

      // * Create a new cached question when a new chunk is detected
      if (questionIndex >= generatedQuestionCacheCountRef.current) {
        handleQuestionCreation(questionType, questionIndex);
      }

      // * Get field IDs for this specific question
      const fieldIds = fieldIdsMapRef.current[questionIndex];
      if (_.isEmpty(fieldIds)) return;

      // * Initialize processed fields set for this question if not exists
      if (!processedFieldsRef.current[questionIndex]) {
        processedFieldsRef.current[questionIndex] = new Set();
      }
      const processedKeys = processedFieldsRef.current[questionIndex];

      // * Process ALL fields sequentially, not just the last one
      const allFields = getAllFieldsFromParsedFields(parseFields);
      _.forEach(allFields, ({ key, value, isComplete }) => {
        // Skip fields already successfully written to cache
        if (isComplete && processedKeys.has(key)) return;

        const success = processAndCreateFields({
          value,
          fieldIds,
          currentKey: key,
          parseFields,
          questionType,
          isQueriesV2Enabled,
        });

        // Only mark complete fields as processed if the cache write succeeded.
        // If the async question template creation hasn't finished yet, the field
        // won't exist in cache and the write silently fails — leaving it unmarked
        // ensures it will be retried on the next render.
        if (isComplete && success) {
          processedKeys.add(key);
        }
      });
    });
  }, [handleQuestionCreation, isQueriesV2Enabled, messages]);

  return null;
};
